import { defu } from 'defu'
import { getCookie, setCookie } from 'h3'
import {
  SAFE_ONBOARDING_ROUTES,
  VENDOR_ONBOARDING_ROUTE
} from '~/stores/vendor/vendor'

export async function useBaseOFetchWithAuth(url, options = {}) {
  const params = defu(options, { headers: {} })
  const headers = { ...params.headers }
  const authStore = import.meta.client ? useAuthStore() : null
  const auth = import.meta.client ? useAuth() : null
  const toast = useCustomToast()
  let event = null
  if (import.meta.server) {
    try {
      event = useRequestEvent()
    } catch (error) {
      // Request event alınamadığında sessizce devam et
      console.warn('Request event could not be retrieved:', error.message)
    }
  }

  if (import.meta.server && event) {
    // Server-side'da sadece anonymous user id'yi al
    if (event) {
      const anonId = getCookie(event, 'anon_user_id')
      if (anonId && !headers['X-Anonymous-User-ID']) {
        headers['X-Anonymous-User-ID'] = anonId
      }
    }
    
    // Server-side'da auth token'ı almaya çalışma - context problemi yaratıyor
    // Auth token'ı sadece client-side'da al
  } else {
    if (auth?.data.value?.accessToken && !headers['Authorization']) {
      const tokenType = auth.data.value?.tokenType || 'Bearer'
      headers['Authorization'] = `${tokenType} ${auth.data.value.accessToken}`
    }
    if (authStore?.anon?.id && !headers['X-Anonymous-User-ID']) {
      headers['X-Anonymous-User-ID'] = authStore.anon.id
    }
  }

  params.headers = headers

  const fullUrl = typeof url === 'string' && url.startsWith('/api/')
    ? url
    : `/api${url.startsWith('/') ? '' : '/'}${url}`

  try {
    const performRequest = async (attempt = 0) => {
      const response = await $fetch(fullUrl, params)

      // Backend middleware may respond with a newly created anonymous user
      if (response?.anonymous_user_id) {
        const anonymousId = String(response.anonymous_user_id)

        if (import.meta.server && event) {
          try {
            setCookie(event, 'anon_user_id', anonymousId, {
              path: '/',
              maxAge: 30 * 24 * 60 * 60
            })
          } catch (error) {
            console.warn('Could not set anonymous user cookie:', error.message)
          }
        }

        if (import.meta.client && authStore) {
          authStore.anon = {
            id: anonymousId,
            name: response.name || null
          }
        }

        headers['X-Anonymous-User-ID'] = anonymousId
        params.headers = headers

        if (attempt < 1) {
          return await performRequest(attempt + 1)
        }
      }

      return response
    }

    return await performRequest()
  } catch (error) {
    const errorPayload = error?.data ?? error?.response?._data ?? {}
    const isVendorNotReady =
      errorPayload?.error_key === 'vendor_not_ready' ||
      errorPayload?.error_code === 3200

    console.error('useBaseOFetchWithAuth error:', errorPayload)

    if (isVendorNotReady && import.meta.client) {
      // Vendor hazır değilse kullanıcıyı onboarding rotasına yönlendir
      toast.warning(
        errorPayload?.message || 'Mağaza bilgilerin eksik. Lütfen tamamlayın.'
      )

      try {
        const vendorStore = useVendorStore()
        vendorStore.markStale()
      } catch (_) {}

      const route = useRoute()
      const isSafeRoute = SAFE_ONBOARDING_ROUTES.some(
        (safe) => route.path === safe || route.path.startsWith(`${safe}/`)
      )

      if (!isSafeRoute) {
        navigateTo(VENDOR_ONBOARDING_ROUTE)
      }
    }

    if (error?.response?.status === 401 && import.meta.client) {
      await auth?.getSession({ forceRefresh: true })
      if (auth?.data.value?.accessToken) {
        const tokenType = auth.data.value?.tokenType || 'Bearer'
        headers['Authorization'] = `${tokenType} ${auth.data.value.accessToken}`
        return await $fetch(fullUrl, { ...params, headers })
      }
      const route = useRoute()
      await auth?.signOut({ redirect: false })
      navigateTo(`/auth?callback=${route.fullPath}`)
    }
    
    throw error
  }
}
